import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CustomPageType } from '../custom-pages/cp-enum';
import { CpRouterDatabuilderService } from '../custom-pages/services/cp-router-databuilder.service';
import { ICustomPageMasterDataResolver } from '../custom-pages/services/master-data';
import { HeaderService } from '../header-information/header-information.service';
import { CpExamination, CpRouterData, CustomPageConfig } from '../models/custom-page';
import { fromServerIsoString } from '../models/interop';
import { VisitExamination } from '../models/visit';
import { SLIDER_OPTIONS } from '../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.state';
import { MainToolbarHeaderInfo } from '../se-toolbar/se-header-toolbar/se-header-info/se-header-info.component';
import { ReportWorkflowService } from './report-workflow.service';
import { IReportWorkflowExamService } from './report-workflow-exam.service';
import { EstensaWaitingService } from './waiting.service';

@Injectable({
    providedIn: 'root',
})
export class ReportingDataBuilder {
    onBack?: (data: any) => Promise<unknown> | undefined;
    private masterDataResolver!: ICustomPageMasterDataResolver;
    private examService!: IReportWorkflowExamService;
    private reportWorkflowService!: ReportWorkflowService;

    constructor(
        private cpRouterDatabuilder: CpRouterDatabuilderService,
        private headerService: HeaderService,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
    ) {
    }

    getMasterDataResolver() {
        return this.masterDataResolver;
    }

    getExamService() {
        return this.examService;
    }

    getReportWorkflowService() {
        return this.reportWorkflowService;
    }

    setServices(
        masterDataResolver: ICustomPageMasterDataResolver,
        examService: IReportWorkflowExamService,
        reportWorkflowService: ReportWorkflowService,
    ) {
        this.masterDataResolver = masterDataResolver;
        this.examService = examService;
        this.reportWorkflowService = reportWorkflowService;
    }

    public async prepareCpParams(
        examinations: CpExamination[],
        masterDataResolver: ICustomPageMasterDataResolver,
        examService: IReportWorkflowExamService,
        reportWorkflowService: ReportWorkflowService,
    ) {
        this.waitingService.isWaiting = true;
        let cpArguments: CpRouterData;
        try {
            const args = await this.cpRouterDatabuilder.buildCpArguments(examinations, (idReport) =>
                examService.getReportExaminations(idReport),
            );
            if (!args || !args.idCustomPageRoot) {
                return;
            }

            cpArguments = { ...args };
        } finally {
            this.waitingService.isWaiting = false;
        }

        let patient = examinations[0].patient;
        let descriptions: string[] = [];
        let examinationDate = null;
        examinations.forEach((item) => {
            if (item.isEcg) {
                descriptions.push(item.exam.description);
            } else {
                // isVisit
                descriptions.unshift(item.exam.description);
            }
            examinationDate = item.examinationDate
                ? fromServerIsoString(item.examinationDate)
                : fromServerIsoString(item.reportDate);
            examinationDate = examinationDate
                ?.toDate()
                .toLocaleDateString(undefined, { hour: '2-digit', minute: '2-digit' });
            if (!item.patient.isLocalPatient) {
                patient = item.patient;
            }
        });
        let procedureInfoDescription = descriptions.join('+');

        const name = patient.firstName || patient?.name;
        const surname = patient.lastName || patient?.surname;

        const patientInfo: MainToolbarHeaderInfo = {
            mainDescription:
                name + ' ' + surname + (!!patient.sexCode ? ' (' + patient.sexCode + ')' : ''),
            secondaryDescription:
                this.translate.instant('BirthDate') + ': ' + new Date(patient.birthDate).toLocaleDateString(),
            infoList: [],
        };
        const procedureInfo: MainToolbarHeaderInfo = {
            mainDescription: procedureInfoDescription,
            infoList: [],
            secondaryDescription: this.translate.instant('ExamDate') + ': ' + examinationDate || '',
        };
        const customPageType = examinations[0].isUs ? CustomPageType.ULTRASOUND : CustomPageType.CARDIOLOGY_VISIT;
        const toolbar = {
            config: new CustomPageConfig(
                customPageType,
                true,
                SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
                SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            ),
            info1: procedureInfo,
            info2: patientInfo,
        };

        this.setServices(masterDataResolver, examService, reportWorkflowService);

        return { ...cpArguments, toolbar };
    }

    public prepareECGParams(examinations: VisitExamination[]) {
        this.headerService.clear();
        const exams = examinations.map((item) => {
            const examinationDate = fromServerIsoString(item.examinationDate).toDate();
            return {
                examinationId: item.id,
                reportId: item.idReport,
                patient: item.patient,
                examinationDate,
                isEcgReport: item.isEcgReport,
                isReported: item.isReported,
                withEcg: true,
                reportingWithoutTrace: item.reportingWithoutTrace,
            };
        });

        return { exams };
    }
}
